import type { Settings } from '@/game/resources'

export type BusName = 'sfx' | 'ambient' | 'music'

export interface ToneOptions {
  bus?: BusName
  type?: OscillatorType
  freq: number
  /** Sweep target; omit for a steady pitch. */
  freqEnd?: number
  duration: number
  gain?: number
  attack?: number
  /** Fraction of the duration spent decaying, 0..1. */
  release?: number
  pan?: number
  detune?: number
  delay?: number
}

export interface NoiseOptions {
  bus?: BusName
  duration: number
  gain?: number
  filter?: BiquadFilterType
  freq?: number
  freqEnd?: number
  q?: number
  attack?: number
  release?: number
  pan?: number
  delay?: number
  /** Playback rate of the noise buffer — higher is hissier. */
  rate?: number
}

/**
 * The whole audio stack: a small WebAudio graph plus primitives for building
 * sounds out of oscillators and noise.
 *
 * Nothing is loaded. Every impact, sweep and drone is synthesised at the moment
 * it plays, which keeps the game asset-free and — more usefully — lets each
 * sound take parameters, so a hard hit is genuinely a different sound from a
 * glancing one rather than the same sample at a different volume.
 */
export class AudioEngine {
  readonly ctx: AudioContext
  private readonly master: GainNode
  private readonly limiter: DynamicsCompressorNode
  private readonly buses: Record<BusName, GainNode>
  private readonly noiseBuffer: AudioBuffer
  private readonly reverb: ConvolverNode
  private readonly reverbSend: GainNode
  private ambientNodes: AudioNode[] = []
  private started = false

  constructor() {
    const Ctor: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext
    this.ctx = new Ctor({ latencyHint: 'interactive' })

    // A limiter on the master keeps a barrel chain-explosion from clipping.
    this.limiter = this.ctx.createDynamicsCompressor()
    this.limiter.threshold.value = -8
    this.limiter.knee.value = 6
    this.limiter.ratio.value = 12
    this.limiter.attack.value = 0.003
    this.limiter.release.value = 0.18

    this.master = this.ctx.createGain()
    this.master.gain.value = 0.8

    this.master.connect(this.limiter)
    this.limiter.connect(this.ctx.destination)

    this.buses = {
      sfx: this.ctx.createGain(),
      ambient: this.ctx.createGain(),
      music: this.ctx.createGain(),
    }
    for (const bus of Object.values(this.buses)) bus.connect(this.master)
    this.buses.ambient.gain.value = 0.5
    this.buses.music.gain.value = 0.45

    this.noiseBuffer = this.createNoiseBuffer(2)
    this.reverb = this.ctx.createConvolver()
    this.reverb.buffer = this.createImpulseResponse(2.4, 2.6)
    this.reverbSend = this.ctx.createGain()
    this.reverbSend.gain.value = 0.22
    this.reverbSend.connect(this.reverb)
    this.reverb.connect(this.master)
  }

  /** Browsers require a user gesture before audio may start. */
  async resume(): Promise<void> {
    if (this.ctx.state === 'suspended') await this.ctx.resume()
    if (!this.started) {
      this.started = true
      this.startAmbience()
    }
  }

  get running(): boolean {
    return this.ctx.state === 'running'
  }

  applySettings(settings: Settings): void {
    this.master.gain.value = settings.masterVolume
    this.buses.sfx.gain.value = settings.sfxVolume
    this.buses.music.gain.value = settings.musicVolume * 0.45
    this.buses.ambient.gain.value = settings.musicVolume * 0.5
  }

  // ───────────────────────────────────────────────────────────── primitives ──

  tone(options: ToneOptions): void {
    if (!this.running) return
    const t = this.ctx.currentTime + (options.delay ?? 0)
    const osc = this.ctx.createOscillator()
    osc.type = options.type ?? 'sine'
    osc.frequency.setValueAtTime(Math.max(1, options.freq), t)
    if (options.freqEnd !== undefined) {
      // Exponential ramps sound like pitch to human ears; linear ones do not.
      osc.frequency.exponentialRampToValueAtTime(
        Math.max(1, options.freqEnd),
        t + options.duration,
      )
    }
    if (options.detune) osc.detune.value = options.detune

    const gainNode = this.envelope(options.gain ?? 0.3, options.duration, t, options)
    osc.connect(gainNode)
    this.route(gainNode, options.bus ?? 'sfx', options.pan ?? 0)

    osc.start(t)
    osc.stop(t + options.duration + 0.05)
  }

  noise(options: NoiseOptions): void {
    if (!this.running) return
    const t = this.ctx.currentTime + (options.delay ?? 0)
    const source = this.ctx.createBufferSource()
    source.buffer = this.noiseBuffer
    source.loop = true
    source.playbackRate.value = options.rate ?? 1

    let node: AudioNode = source
    if (options.filter) {
      const filter = this.ctx.createBiquadFilter()
      filter.type = options.filter
      filter.frequency.setValueAtTime(Math.max(20, options.freq ?? 1000), t)
      if (options.freqEnd !== undefined) {
        filter.frequency.exponentialRampToValueAtTime(
          Math.max(20, options.freqEnd),
          t + options.duration,
        )
      }
      filter.Q.value = options.q ?? 1
      source.connect(filter)
      node = filter
    }

    const gainNode = this.envelope(options.gain ?? 0.25, options.duration, t, options)
    node.connect(gainNode)
    this.route(gainNode, options.bus ?? 'sfx', options.pan ?? 0)

    source.start(t)
    source.stop(t + options.duration + 0.05)
  }

  /** Sends a fraction of a sound into the shared reverb tail. */
  private route(node: GainNode, bus: BusName, pan: number): void {
    const panner = this.ctx.createStereoPanner()
    panner.pan.value = Math.max(-1, Math.min(1, pan))
    node.connect(panner)
    panner.connect(this.buses[bus])
    if (bus === 'sfx') panner.connect(this.reverbSend)
  }

  private envelope(
    peak: number,
    duration: number,
    startTime: number,
    options: { attack?: number; release?: number },
  ): GainNode {
    const gain = this.ctx.createGain()
    const attack = Math.min(options.attack ?? 0.005, duration * 0.5)
    const release = duration * (options.release ?? 0.85)

    gain.gain.setValueAtTime(0.0001, startTime)
    gain.gain.linearRampToValueAtTime(peak, startTime + attack)
    gain.gain.exponentialRampToValueAtTime(
      0.0001,
      startTime + Math.max(attack + 0.01, release),
    )
    return gain
  }

  // ──────────────────────────────────────────────────────────────── ambience ──

  /**
   * Two continuous layers: wind (filtered noise whose band wanders) and the
   * void itself (a pair of detuned sub-oscillators that beat slowly against
   * each other). Together they fill the silence between fights without ever
   * competing with an impact.
   */
  private startAmbience(): void {
    const t = this.ctx.currentTime

    const wind = this.ctx.createBufferSource()
    wind.buffer = this.noiseBuffer
    wind.loop = true

    const windFilter = this.ctx.createBiquadFilter()
    windFilter.type = 'bandpass'
    windFilter.frequency.value = 420
    windFilter.Q.value = 0.8

    const windGain = this.ctx.createGain()
    windGain.gain.value = 0.12

    // Slow LFO on the filter band makes the wind gust instead of hiss.
    const lfo = this.ctx.createOscillator()
    lfo.frequency.value = 0.07
    const lfoGain = this.ctx.createGain()
    lfoGain.gain.value = 260
    lfo.connect(lfoGain)
    lfoGain.connect(windFilter.frequency)

    wind.connect(windFilter)
    windFilter.connect(windGain)
    windGain.connect(this.buses.ambient)

    wind.start(t)
    lfo.start(t)

    const droneGain = this.ctx.createGain()
    droneGain.gain.value = 0.16
    droneGain.connect(this.buses.music)

    for (const freq of [38, 38.6, 57]) {
      const osc = this.ctx.createOscillator()
      osc.type = freq > 50 ? 'triangle' : 'sine'
      osc.frequency.value = freq
      const g = this.ctx.createGain()
      g.gain.value = freq > 50 ? 0.25 : 1
      osc.connect(g)
      g.connect(droneGain)
      osc.start(t)
      this.ambientNodes.push(osc)
    }

    this.ambientNodes.push(wind, lfo)
  }

  // ───────────────────────────────────────────────────────────────── buffers ──

  private createNoiseBuffer(seconds: number): AudioBuffer {
    const length = Math.floor(this.ctx.sampleRate * seconds)
    const buffer = this.ctx.createBuffer(1, length, this.ctx.sampleRate)
    const data = buffer.getChannelData(0)
    // Pink-ish noise: cheaper than a proper filter and kinder to the ears than
    // white noise, which is what most "wind" ends up sounding like.
    let b0 = 0
    let b1 = 0
    let b2 = 0
    for (let i = 0; i < length; i++) {
      const white = Math.random() * 2 - 1
      b0 = 0.99765 * b0 + white * 0.099
      b1 = 0.963 * b1 + white * 0.2965
      b2 = 0.57 * b2 + white * 1.0526
      data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.28
    }
    return buffer
  }

  private createImpulseResponse(seconds: number, decay: number): AudioBuffer {
    const rate = this.ctx.sampleRate
    const length = Math.floor(rate * seconds)
    const buffer = this.ctx.createBuffer(2, length, rate)
    for (let channel = 0; channel < 2; channel++) {
      const data = buffer.getChannelData(channel)
      for (let i = 0; i < length; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay)
      }
    }
    return buffer
  }
}
